'use client';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Cookies from 'js-cookie';
import Head from 'next/head';
import { useEffect, type FC, type ReactNode } from 'react';
import type { Settings } from 'src/types/settings';
import { NextAppDirEmotionCacheProvider } from 'tss-react/next/appDir';
import HyperDX from '@hyperdx/browser';

// Remove if locales are not used
import 'src/locales/i18n';

import { SettingsDrawer, SplashScreen, Toaster, createTheme, useAnalytics, usePathname } from '@zolo/ui';
import RTL from '@zolo/ui/components/layout/rtl';

import { useRouter } from 'next/navigation';
import { gtmConfig, hyperDXConfig } from '~/config';
import { SettingsConsumer, SettingsProvider } from '~/state/contexts/settings';
import { useCustomizedUser } from '~/state/hooks/use-session';
import { useGetOrganisation } from '~/state/api/v2';

const SETTINGS_STORAGE_KEY = 'app.settings';

const resetSettings = (): void => {
  try {
    Cookies.remove(SETTINGS_STORAGE_KEY);
    window.location.reload();
  } catch (err) {
    console.error(err);
  }
};

const updateSettings = (settings: Settings): void => {
  try {
    Cookies.set(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    window.location.reload();
  } catch (err) {
    console.error(err);
  }
};

interface LayoutProps {
  children: ReactNode;
  settings?: Settings;
}

export const Layout: FC<LayoutProps> = (props: LayoutProps) => {
  const { user, isLoaded } = useCustomizedUser();
  const router = useRouter();
  const publicPages = [
    '/auth/login',
    '/api(.*)',
    '/auth/forgot-password',
    '/auth/register',
    '/auth/reset-password',
    '/auth/register-invited-user',
    '/auth/verify-invited-user',
    '/auth/verify-code',
    '/onboarding/about-you',
    '/auth/update-password',
    '/restaurants',
  ];

  const path = usePathname();
  const isPublicPage = publicPages.includes(path);

  const { children, settings } = props;

  useAnalytics(gtmConfig);

  const { data: organization } = useGetOrganisation();

  useEffect(() => {
    if (typeof window !== 'undefined' && hyperDXConfig.apiKey) {
      HyperDX.init({
        apiKey: hyperDXConfig.apiKey,
        service: hyperDXConfig.serviceName || 'SERVICE_NAME',
        advancedNetworkCapture: true,
        maskAllInputs: false,
        maskAllText: false,
        disableReplay:
          process.env.NEXT_PUBLIC_HYPERDX_REPLAY_ENABLED == 'false' || !process.env.NEXT_PUBLIC_HYPERDX_REPLAY_ENABLED,
      });
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (!path.includes('order-details') && !path.includes('restaurants')) {
      if (path === '/') {
        if (user && user.id) {
          if (organization?.org_config?.feature_gate?.chat_v2) {
            router.push('/dashboard/chat-v2');
          } else {
            router.push('/dashboard/chat');
          }
        } else {
          router.push('/auth/login');
        }
      } else {
        if (user && user.id) {
          if (isPublicPage) {
            router.push('/dashboard/chat');
          }
        } else {
          if (!isPublicPage) {
            router.push('/auth/login');
          }
        }
      }
    }
  }, [path, isPublicPage, router, user, isLoaded, organization]);

  return (
    <NextAppDirEmotionCacheProvider options={{ key: 'css' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SettingsProvider onReset={resetSettings} onUpdate={updateSettings} settings={settings}>
          <SettingsConsumer>
            {(settings) => {
              const theme = createTheme({
                colorPreset: settings.colorPreset,
                contrast: settings.contrast,
                direction: settings.direction,
                paletteMode: settings.paletteMode,
                responsiveFontSizes: settings.responsiveFontSizes,
              });

              return (
                <ThemeProvider theme={theme}>
                  <Head>
                    <meta name='color-scheme' content={settings.paletteMode} />
                    <meta name='theme-color' content={theme.palette.neutral[900]} />
                  </Head>
                  <RTL direction={settings.direction}>
                    <CssBaseline />
                    {!isLoaded ? (
                      <SplashScreen />
                    ) : (
                      <>
                        {children}
                        <SettingsDrawer
                          canReset={settings.isCustom}
                          onClose={settings.handleDrawerClose}
                          onReset={settings.handleReset}
                          onUpdate={settings.handleUpdate}
                          open={settings.openDrawer}
                          values={{
                            colorPreset: settings.colorPreset,
                            contrast: settings.contrast,
                            direction: settings.direction,
                            paletteMode: settings.paletteMode,
                            responsiveFontSizes: settings.responsiveFontSizes,
                            stretch: settings.stretch,
                            layout: settings.layout,
                            navColor: settings.navColor,
                          }}
                        />
                      </>
                    )}
                    <Toaster />
                  </RTL>
                </ThemeProvider>
              );
            }}
          </SettingsConsumer>
        </SettingsProvider>
      </LocalizationProvider>
    </NextAppDirEmotionCacheProvider>
  );
};
